import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { STORY_UI } from '../../solution-stories/storyUiTokens';
import { SEARCE_COLORS } from '../../../theme';
import { formatUsdCompact, type GroupedSplitRow } from '../../../utils/billingReportMetrics';

export interface BillingReportSplitBarChartProps {
  data: GroupedSplitRow[];
  height?: number;
}

export function BillingReportSplitBarChart({
  data,
  height = 320,
}: BillingReportSplitBarChartProps): React.ReactElement {
  if (data.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" sx={{ color: STORY_UI.inkFaint }}>
          No data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 8 }} barGap={4}>
          <CartesianGrid strokeDasharray="3 3" stroke={STORY_UI.line} vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: STORY_UI.inkFaint, fontFamily: STORY_UI.fontSans }}
            axisLine={{ stroke: STORY_UI.line }}
            tickLine={false}
            interval={0}
            angle={-20}
            textAnchor="end"
            height={56}
          />
          <YAxis
            tickFormatter={(v) => formatUsdCompact(Number(v))}
            tick={{ fontSize: 11, fill: STORY_UI.inkFaint, fontFamily: STORY_UI.fontSans }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <RechartsTooltip
            formatter={(value: number) => formatUsdCompact(value)}
            contentStyle={{
              borderRadius: 8,
              border: `1px solid ${STORY_UI.line}`,
              fontFamily: STORY_UI.fontSans,
              fontSize: 12,
            }}
          />
          <Legend wrapperStyle={{ fontFamily: STORY_UI.fontSans, fontSize: 12 }} />
          <Bar dataKey="budget" name="Budget" fill={STORY_UI.accentTint} stroke={STORY_UI.accent} radius={[4, 4, 0, 0]} />
          <Bar dataKey="spend" name="Spend" fill={SEARCE_COLORS.secondary} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
